/**
 * Source: any app's own listing of products, for the "App Listing" source type of a Product Query section.
 *
 * Same role as WishlistSource, but the method is named by the section (Records Source) so websitehq knows no app. The
 * method answers {items: [listing rows], settings} in the shape webshop_source / wishlist_listing use, so adaptItems()
 * and the whole decoration batch (swatches, sizes, badges, offers, media, ratings) are reused as they are: a product
 * card looks and behaves the same wherever it appears, whichever source fetched it.
 */

import { adaptItems } from './webshop.js';
import { adaptProfiles } from './profile.js';
import { fetchDecorations, routesAndCodes } from './webshop_source.js';
import { getSwatchColorOverrides } from '../swatch_color_overrides.js';
import { getAttributeIcons } from '../attribute_icons.js';

const METHOD_OK = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)+$/;

function call(method, args) {
	return new Promise((resolve) => {
		let settled = false;
		const done = (value) => { if (settled) return; settled = true; resolve(value); };
		if (typeof frappe === 'undefined' || !frappe.call || !METHOD_OK.test(method || '')) { done(null); return; }
		let req;
		try { req = frappe.call({ method, args, callback: (r) => done(r && r.message), error: () => done(null) }); } catch (err) { done(null); return; }
		if (req && typeof req.always === 'function') req.always(() => setTimeout(() => done(null), 0));
		else setTimeout(() => done(null), 15000);
	});
}

export class AppListingSource {
	/**
	 * @param {object} opts
	 * @param {string} opts.method dotted path of the app's listing method
	 * @param {object} [opts.args] values passed to it (for example from the page address)
	 * @param {CartStore} [opts.cartStore]
	 */
	constructor(opts) {
		this.method = (opts || {}).method;
		this.args = (opts || {}).args || {};
		this.cartStore = (opts || {}).cartStore || null;
	}

	/** Not paginated: an app's own list is small enough to fetch whole. */
	async fetch(query) {
		const start = (query && query.start) || 0;
		if (start > 0) return { models: [], settings: {}, pagination: { hasMore: false } };
		const first = await call(this.method, this.args);
		if (first && first.kind === 'profile') {  // a listing of people: the rows are drawn as profile cards and need no product decoration
			const rows = first.items || [];
			this.filters = first.filters || [];  // what the app lets this listing be narrowed by; the filter panel draws them (filters/catalog_filters.js)
			return { models: adaptProfiles(rows), settings: {}, pagination: { hasMore: false, totalCount: rows.length, countReliable: true } };
		}
		const [result, swatchColorOverrides, attributeIcons] = await Promise.all([call(this.method, this.args), getSwatchColorOverrides(), getAttributeIcons()]);
		const items = (result && result.items) || [];
		const settings = (result && result.settings) || {};
		settings.swatchColorOverrides = swatchColorOverrides;
		settings.attributeIcons = attributeIcons;
		const { routes, itemCodes } = routesAndCodes(items);
		const decorations = await fetchDecorations(routes, itemCodes, items, this.cartStore);
		return {
			models: adaptItems(items, settings, decorations),
			settings,
			pagination: { hasMore: false, totalCount: items.length, countReliable: true },
		};
	}
}
